import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { Calendar, MapPin, Navigation, Share2, Store } from "lucide-react";
import { api, mediaUrl } from "../lib/api";
import { TruckCard } from "../components/TruckCard";
import { MapView } from "../components/MapView";
import { Seo } from "../components/Seo";
import { Button } from "../components/ui/button";
import { toast } from "sonner";

export default function MarketDetail() {
  const { slug } = useParams();
  const nav = useNavigate();
  const [m, setM] = useState(null);
  const [err, setErr] = useState(false);

  useEffect(() => { api.get(`/markets/${slug}`).then((r) => setM(r.data)).catch(() => setErr(true)); }, [slug]);

  if (err) return (
    <div className="max-w-3xl mx-auto px-6 py-24 text-center">
      <h1 className="font-display font-bold text-2xl text-urban-teal">Marché introuvable</h1>
      <Button onClick={() => nav("/marches")} className="rounded-full bg-street-gold text-white font-bold mt-5">Voir tous les marchés</Button>
    </div>
  );
  if (!m) return <p className="text-center py-24 text-urban-teal/50">Chargement...</p>;

  const trucks = m.trucks || [];
  const itineraire = () => {
    const q = encodeURIComponent(m.address || `${m.name} ${m.city}`);
    window.open(`https://www.google.com/maps/search/?api=1&query=${q}`, "_blank");
  };
  const share = () => { navigator.clipboard.writeText(window.location.href); toast.success("Lien copié !"); };

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "Event",
    name: m.name,
    ...(m.description ? { description: m.description } : {}),
    ...(m.photo ? { image: [mediaUrl(m.photo)] } : {}),
    location: {
      "@type": "Place",
      name: m.address || m.city || m.name,
      address: { "@type": "PostalAddress", addressLocality: m.city || "", addressCountry: "FR" },
      ...(m.lat && m.lng ? { geo: { "@type": "GeoCoordinates", latitude: m.lat, longitude: m.lng } } : {}),
    },
    url: `${(process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "")}/marche/${slug}`,
  };

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 py-8">
      <Seo
        title={`${m.name}${m.city ? " - " + m.city : ""}`}
        description={m.description ? m.description.slice(0, 160) : `${m.name} : ${trucks.length} foodtruck(s) réunis${m.city ? " à " + m.city : ""}. Horaires, itinéraire et camions présents sur StreatEat.`}
        path={`/marche/${slug}`}
        type="article"
        image={m.photo ? mediaUrl(m.photo) : undefined}
        jsonLd={jsonLd}
      />

      <div className="rounded-3xl overflow-hidden shadow-warm bg-white">
        <div className="h-52 sm:h-72 relative">
          {m.photo ? (
            <img src={mediaUrl(m.photo)} alt={m.name} className="w-full h-full object-cover" data-testid="market-hero-photo" />
          ) : (
            <div className="w-full h-full flex items-center justify-center bg-urban-teal/5"><Store size={72} className="text-urban-teal/25" /></div>
          )}
          <span className="absolute top-4 left-4 inline-flex items-center gap-1 px-3 py-1 rounded-full bg-street-gold text-white text-xs font-bold"><Calendar size={13} /> Événement</span>
        </div>
      </div>

      <div className="mt-6">
        <h1 className="text-3xl sm:text-4xl font-display font-extrabold text-urban-teal" data-testid="market-title">{m.name}</h1>
        <div className="flex flex-wrap items-center gap-3 mt-2 text-urban-teal/60">
          {m.city && <span className="flex items-center gap-1"><MapPin size={16} /> {m.address || m.city}</span>}
          {m.schedule && <span className="flex items-center gap-1"><Calendar size={16} /> {m.schedule}</span>}
        </div>
        {m.description && <p className="text-slate-600 mt-4 leading-relaxed">{m.description}</p>}

        <div className="flex flex-wrap gap-2 mt-4">
          {(m.address || m.city) && (
            <Button onClick={itineraire} className="rounded-full bg-urban-teal text-white font-bold gap-1" data-testid="market-directions-btn"><Navigation size={15} /> Itinéraire</Button>
          )}
          <Button onClick={share} variant="outline" className="rounded-full border-urban-teal/20 text-urban-teal font-bold gap-1" data-testid="market-share-btn"><Share2 size={15} /> Partager</Button>
        </div>
      </div>

      {(trucks.some((t) => t.lat != null && t.lng != null) || (m.lat != null && m.lng != null)) && (
        <div className="mt-6" data-testid="market-map">
          <MapView trucks={trucks} center={m.lat != null && m.lng != null ? [m.lat, m.lng] : undefined} height={300} />
        </div>
      )}

      <h2 className="font-display font-bold text-xl text-urban-teal mt-8 mb-4">Foodtrucks présents ({trucks.length})</h2>
      {trucks.length === 0 ? (
        <p className="text-sm text-urban-teal/50" data-testid="market-trucks-empty">Les foodtrucks participants seront annoncés prochainement.</p>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="market-trucks-grid">
          {trucks.map((t, i) => <TruckCard key={t.slug} truck={t} index={i} />)}
        </div>
      )}

      <Link to="/marches" className="block text-center text-street-gold font-bold text-sm mt-10">← Tous les marchés</Link>
    </div>
  );
}
